import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import { formatDate } from '../utils/date';

const STATUS_OPTIONS = ['draft', 'submitted', 'approved', 'rejected', 'paid'];
const EXPENSE_TYPES = ['Travel', 'Meals', 'Accommodation', 'Office Supplies', 'Software', 'Training', 'Communication', 'Other'];
const EMPTY_ITEM = { expense_type: '', description: '', amount: '', date: '' };

export default function Reimbursements() {
  const [tab, setTab] = useState('all');

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'all', label: 'All Claims' },
          { key: 'my', label: 'My Claims' },
        ].map(t => (
          <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      {tab === 'all' && <AllClaims />}
      {tab === 'my' && <MyClaims />}
    </div>
  );
}

/* =================================================================
   All Claims Tab (Admin/Manager view)
   ================================================================= */
function AllClaims() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [detailClaim, setDetailClaim] = useState(null);
  const [items, setItems] = useState([]);

  const fetchClaims = useCallback(async () => {
    try {
      let url = '/api/reimbursements';
      if (filterStatus) url += `?status=${filterStatus}`;
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setClaims(data.reimbursements || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, [filterStatus]);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  async function fetchDetail(claim) {
    setDetailClaim(claim);
    try {
      const res = await apiFetch(`/api/reimbursements/${claim.id}/items`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (e) { /* handled */ }
  }

  async function handleAction(id, action) {
    const msg = action === 'approve' ? 'Approve this claim?' : action === 'reject' ? 'Reject this claim?' : 'Mark as paid?';
    if (!confirm(msg)) return;
    await apiFetch(`/api/reimbursements/${id}/${action}`, { method: 'POST', body: JSON.stringify({}) });
    fetchClaims();
    if (detailClaim?.id === id) {
      const updated = { ...detailClaim, status: action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'paid' };
      setDetailClaim(updated);
    }
  }

  if (detailClaim) {
    return (
      <ClaimDetail
        claim={detailClaim}
        items={items}
        onBack={() => setDetailClaim(null)}
        onAction={handleAction}
        showActions={true}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <select className="form-control" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 160 }}>
          <option value="">All Status</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Claim #</th><th>Employee</th><th>Title</th><th style={{ textAlign: 'right' }}>Amount</th><th>Currency</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center' }}>Loading...</td></tr>
            ) : claims.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>No claims</td></tr>
            ) : claims.map(c => (
              <tr key={c.id}>
                <td><a href="#" onClick={e => { e.preventDefault(); fetchDetail(c); }} style={{ color: '#6366f1', fontWeight: 500 }}>{c.reimbursement_number}</a></td>
                <td>{c.first_name ? `${c.first_name} ${c.last_name}` : '-'}</td>
                <td>{c.title || '-'}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{parseFloat(c.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td>{c.currency || 'INR'}</td>
                <td><span className={`badge ${statusBadge(c.status)}`}>{c.status}</span></td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => fetchDetail(c)} style={{ marginRight: 4 }}>View</button>
                  {c.status === 'submitted' && (
                    <>
                      <button className="btn btn-sm btn-primary" onClick={() => handleAction(c.id, 'approve')} style={{ marginRight: 4 }}>Approve</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleAction(c.id, 'reject')}>Reject</button>
                    </>
                  )}
                  {c.status === 'approved' && (
                    <button className="btn btn-sm btn-primary" onClick={() => handleAction(c.id, 'pay')}>Pay</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =================================================================
   My Claims Tab
   ================================================================= */
function MyClaims() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', currency: 'INR' });
  const [employees, setEmployees] = useState([]);
  const [detailClaim, setDetailClaim] = useState(null);
  const [items, setItems] = useState([]);
  const [itemModal, setItemModal] = useState(false);
  const [itemForm, setItemForm] = useState({ ...EMPTY_ITEM });

  const fetchMyClaims = useCallback(async () => {
    try {
      const res = await apiFetch('/api/reimbursements/my');
      if (res.ok) {
        const data = await res.json();
        setClaims(data.reimbursements || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMyClaims(); }, [fetchMyClaims]);

  async function fetchDetail(claim) {
    setDetailClaim(claim);
    try {
      const res = await apiFetch(`/api/reimbursements/${claim.id}/items`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (e) { /* handled */ }
  }

  async function handleCreate() {
    try {
      // Get current user's employee id
      const empRes = await apiFetch('/api/employees?limit=1&self=true');
      let employeeId = null;
      if (empRes.ok) {
        const empData = await empRes.json();
        const emps = empData.employees || empData.data || [];
        if (emps.length > 0) employeeId = emps[0].id;
      }

      const res = await apiFetch('/api/reimbursements', {
        method: 'POST',
        body: JSON.stringify({ ...createForm, employee_id: employeeId })
      });
      if (res.ok) {
        setCreateModal(false);
        setCreateForm({ title: '', currency: 'INR' });
        fetchMyClaims();
      }
    } catch (e) { /* handled */ }
  }

  async function handleAddItem() {
    if (!detailClaim) return;
    try {
      const res = await apiFetch(`/api/reimbursements/${detailClaim.id}/items`, {
        method: 'POST',
        body: JSON.stringify({ ...itemForm, amount: parseFloat(itemForm.amount) || 0 })
      });
      if (res.ok) {
        setItemModal(false);
        setItemForm({ ...EMPTY_ITEM });
        fetchDetail(detailClaim);
        fetchMyClaims();
      }
    } catch (e) { /* handled */ }
  }

  async function handleDeleteItem(itemId) {
    if (!confirm('Delete this item?')) return;
    await apiFetch(`/api/reimbursements/${detailClaim.id}/items/${itemId}`, { method: 'DELETE' });
    fetchDetail(detailClaim);
    fetchMyClaims();
  }

  async function handleSubmit(id) {
    if (!confirm('Submit this claim for approval?')) return;
    await apiFetch(`/api/reimbursements/${id}/submit`, { method: 'POST' });
    fetchMyClaims();
    if (detailClaim?.id === id) setDetailClaim({ ...detailClaim, status: 'submitted' });
  }

  if (detailClaim) {
    return (
      <div>
        <button className="btn btn-secondary" onClick={() => setDetailClaim(null)} style={{ marginBottom: 16 }}>Back to My Claims</button>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h4>{detailClaim.reimbursement_number} - {detailClaim.title || 'Untitled'}</h4>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className={`badge ${statusBadge(detailClaim.status)}`}>{detailClaim.status}</span>
              {detailClaim.status === 'draft' && <button className="btn btn-sm btn-primary" onClick={() => handleSubmit(detailClaim.id)}>Submit for Approval</button>}
            </div>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div><strong>Total:</strong> {parseFloat(detailClaim.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} {detailClaim.currency || 'INR'}</div>
            <div><strong>Created:</strong> {formatDate(detailClaim.created_at, '-')}</div>
            <div><strong>Submitted:</strong> {formatDate(detailClaim.submitted_at, '-')}</div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Expense Items</h4>
            {detailClaim.status === 'draft' && <button className="btn btn-sm btn-primary" onClick={() => { setItemForm({ ...EMPTY_ITEM }); setItemModal(true); }}>+ Add Item</button>}
          </div>
          <table className="data-table">
            <thead><tr><th>Type</th><th>Description</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th>{detailClaim.status === 'draft' && <th>Actions</th>}</tr></thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={detailClaim.status === 'draft' ? 5 : 4} style={{ textAlign: 'center', color: '#94a3b8' }}>No items - add expense items to this claim</td></tr>
              ) : items.map(item => (
                <tr key={item.id}>
                  <td>{item.expense_type}</td>
                  <td>{item.description || '-'}</td>
                  <td>{item.date || '-'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{parseFloat(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  {detailClaim.status === 'draft' && (
                    <td><button className="btn btn-sm btn-danger" onClick={() => handleDeleteItem(item.id)}>Delete</button></td>
                  )}
                </tr>
              ))}
              {items.length > 0 && (
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                  <td colSpan={3}>Total</td>
                  <td style={{ textAlign: 'right' }}>{items.reduce((s, i) => s + parseFloat(i.amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  {detailClaim.status === 'draft' && <td></td>}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {itemModal && (
          <div className="modal-overlay" onClick={() => setItemModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
              <div className="modal-header"><h3>Add Expense Item</h3><button className="modal-close" onClick={() => setItemModal(false)}>X</button></div>
              <div className="modal-body">
                <div className="form-group"><label>Expense Type *</label>
                  <select className="form-control" value={itemForm.expense_type} onChange={e => setItemForm({ ...itemForm, expense_type: e.target.value })}>
                    <option value="">Select type</option>
                    {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Description</label><input className="form-control" value={itemForm.description} onChange={e => setItemForm({ ...itemForm, description: e.target.value })} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group"><label>Amount *</label><input type="number" className="form-control" value={itemForm.amount} onChange={e => setItemForm({ ...itemForm, amount: e.target.value })} step="0.01" /></div>
                  <div className="form-group"><label>Date</label><input type="date" className="form-control" value={itemForm.date} onChange={e => setItemForm({ ...itemForm, date: e.target.value })} /></div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setItemModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleAddItem} disabled={!itemForm.expense_type || !itemForm.amount}>Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ New Claim</button>
      </div>

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Claim #</th><th>Title</th><th style={{ textAlign: 'right' }}>Amount</th><th>Currency</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center' }}>Loading...</td></tr>
            ) : claims.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>No claims yet</td></tr>
            ) : claims.map(c => (
              <tr key={c.id}>
                <td><a href="#" onClick={e => { e.preventDefault(); fetchDetail(c); }} style={{ color: '#6366f1', fontWeight: 500 }}>{c.reimbursement_number}</a></td>
                <td>{c.title || '-'}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{parseFloat(c.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td>{c.currency || 'INR'}</td>
                <td><span className={`badge ${statusBadge(c.status)}`}>{c.status}</span></td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => fetchDetail(c)} style={{ marginRight: 4 }}>View</button>
                  {c.status === 'draft' && <button className="btn btn-sm btn-primary" onClick={() => handleSubmit(c.id)}>Submit</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createModal && (
        <div className="modal-overlay" onClick={() => setCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
            <div className="modal-header"><h3>New Reimbursement Claim</h3><button className="modal-close" onClick={() => setCreateModal(false)}>X</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Title *</label><input className="form-control" value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })} placeholder="e.g. March Travel Expenses" /></div>
              <div className="form-group"><label>Currency</label>
                <select className="form-control" value={createForm.currency} onChange={e => setCreateForm({ ...createForm, currency: e.target.value })}>
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
              <p style={{ color: '#64748b', fontSize: 13 }}>Create the claim first, then add individual expense items.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!createForm.title}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================================================================
   Claim Detail (shared)
   ================================================================= */
function ClaimDetail({ claim, items, onBack, onAction, showActions }) {
  return (
    <div>
      <button className="btn btn-secondary" onClick={onBack} style={{ marginBottom: 16 }}>Back</button>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <h4>{claim.reimbursement_number} - {claim.title || 'Untitled'}</h4>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className={`badge ${statusBadge(claim.status)}`}>{claim.status}</span>
            {showActions && claim.status === 'submitted' && (
              <>
                <button className="btn btn-sm btn-primary" onClick={() => onAction(claim.id, 'approve')}>Approve</button>
                <button className="btn btn-sm btn-danger" onClick={() => onAction(claim.id, 'reject')}>Reject</button>
              </>
            )}
            {showActions && claim.status === 'approved' && (
              <button className="btn btn-sm btn-primary" onClick={() => onAction(claim.id, 'pay')}>Mark Paid</button>
            )}
          </div>
        </div>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div><strong>Employee:</strong> {claim.first_name ? `${claim.first_name} ${claim.last_name}` : '-'}</div>
          <div><strong>Total:</strong> {parseFloat(claim.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} {claim.currency || 'INR'}</div>
          <div><strong>Status:</strong> {claim.status}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h4>Expense Items</h4></div>
        <table className="data-table">
          <thead><tr><th>Type</th><th>Description</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No items</td></tr>
            ) : items.map(item => (
              <tr key={item.id}>
                <td>{item.expense_type}</td>
                <td>{item.description || '-'}</td>
                <td>{item.date || '-'}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{parseFloat(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>
            ))}
            {items.length > 0 && (
              <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                <td colSpan={3}>Total</td>
                <td style={{ textAlign: 'right' }}>{items.reduce((s, i) => s + parseFloat(i.amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =================================================================
   Helpers
   ================================================================= */
function statusBadge(status) {
  switch (status) {
    case 'paid': return 'badge-green';
    case 'approved': return 'badge-blue';
    case 'submitted': return 'badge-yellow';
    case 'rejected': return 'badge-red';
    default: return 'badge-gray';
  }
}
